import { useEffect, useRef, useState, useCallback } from 'react';

// ── Types ─────────────────────────────────────────────────────────────────────

export type AutoSaveStatus = 'idle' | 'pending' | 'saving' | 'saved' | 'error';

export interface UseAutoSaveOptions<T> {
  /** Données à surveiller */
  data: T;
  /** Fonction de sauvegarde (appel API) — retourne une Promise */
  saveFn: (data: T) => Promise<void>;
  /** Délai debounce en ms (défaut: 2000) */
  delay?: number;
  /** Activer/désactiver l'auto-save (ex: canEdit) */
  enabled?: boolean;
}

export interface UseAutoSaveReturn {
  /** État actuel */
  status: AutoSaveStatus;
  /** Message d'erreur si status === 'error' */
  error: string | null;
  /** Erreurs de validation structurées (422) */
  validationErrors: Record<string, string[]> | null;
  /** Force un save immédiat (pour flush avant navigation) */
  flush: () => Promise<void>;
  /** Reset l'état d'erreur */
  clearError: () => void;
}

// ── Hook ──────────────────────────────────────────────────────────────────────

export function useAutoSave<T>({
  data,
  saveFn,
  delay = 2000,
  enabled = true,
}: UseAutoSaveOptions<T>): UseAutoSaveReturn {
  const [status, setStatus] = useState<AutoSaveStatus>('idle');
  const [error, setError] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<Record<string, string[]> | null>(null);

  // Refs pour éviter les closures stales
  const dataRef = useRef<T>(data);
  const saveFnRef = useRef(saveFn);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const isFirstRender = useRef(true);
  const isSavingRef = useRef(false);
  const pendingDataRef = useRef<T | null>(null);
  const savedTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const flushResolveRef = useRef<(() => void) | null>(null);
  const mountedRef = useRef(true);

  // Mettre à jour les refs quand les props changent
  dataRef.current = data;
  saveFnRef.current = saveFn;

  // Cleanup on unmount
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (timerRef.current) clearTimeout(timerRef.current);
      if (savedTimerRef.current) clearTimeout(savedTimerRef.current);
      if (abortRef.current) abortRef.current.abort();
    };
  }, []);

  // Exécute le save effectif avec retry
  const executeSave = useCallback(async (dataToSave: T, retryCount = 0): Promise<void> => {
    if (!mountedRef.current) return;

    isSavingRef.current = true;
    setStatus('saving');
    setError(null);
    setValidationErrors(null);

    // AbortController pour annuler le fetch si unmount
    const controller = new AbortController();
    abortRef.current = controller;

    try {
      await saveFnRef.current(dataToSave);

      if (!mountedRef.current) return;

      isSavingRef.current = false;
      setStatus('saved');

      // Resolve flush si en attente
      if (flushResolveRef.current) {
        flushResolveRef.current();
        flushResolveRef.current = null;
      }

      // Retour à idle après 3 secondes
      if (savedTimerRef.current) clearTimeout(savedTimerRef.current);
      savedTimerRef.current = setTimeout(() => {
        if (mountedRef.current) setStatus('idle');
      }, 3000);

    } catch (err: any) {
      if (!mountedRef.current) return;

      // Abort silencieux (unmount)
      if (err?.name === 'AbortError') return;

      // 401 → redirect login
      if (err?.status === 401) {
        window.location.href = '/login';
        return;
      }

      // 422 → erreur de validation
      if (err?.status === 422) {
        isSavingRef.current = false;
        setStatus('error');
        setError(err?.message ?? 'Erreur de validation');
        setValidationErrors(err?.errors ?? null);
        if (flushResolveRef.current) {
          flushResolveRef.current();
          flushResolveRef.current = null;
        }
        return;
      }

      // 5xx ou réseau → retry (max 3 tentatives)
      if (retryCount < 3) {
        await new Promise((resolve) => setTimeout(resolve, 5000));
        if (!mountedRef.current) return;
        return executeSave(dataToSave, retryCount + 1);
      }

      // Échec final après 3 retries
      isSavingRef.current = false;
      setStatus('error');
      setError(err?.message ?? 'Erreur réseau — impossible de sauvegarder');
      if (flushResolveRef.current) {
        flushResolveRef.current();
        flushResolveRef.current = null;
      }
    }
  }, []);

  // Planifie un save debounced
  const scheduleSave = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    setStatus('pending');
    timerRef.current = setTimeout(() => {
      if (!mountedRef.current) return;
      const currentData = dataRef.current;
      executeSave(currentData);
    }, delay);
  }, [delay, executeSave]);

  // Détecte les changements de données via JSON.stringify
  const prevJsonRef = useRef<string>('');

  useEffect(() => {
    const json = JSON.stringify(data);

    // Ignorer le premier rendu
    if (isFirstRender.current) {
      isFirstRender.current = false;
      prevJsonRef.current = json;
      return;
    }

    // Pas de changement réel
    if (json === prevJsonRef.current) return;
    prevJsonRef.current = json;

    // Si disabled, ne rien faire
    if (!enabled) return;

    // Si un save est en cours, mémoriser les données pending
    if (isSavingRef.current) {
      pendingDataRef.current = data;
      setStatus('pending');
      return;
    }

    scheduleSave();
  }, [data, enabled, scheduleSave]);

  // Flush : annule le debounce et exécute immédiatement
  const flush = useCallback((): Promise<void> => {
    // Annuler le timer debounce
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }

    // Si pas de pending changes (idle ou saved) → resolve immédiatement
    if (status !== 'pending' && !isSavingRef.current) {
      return Promise.resolve();
    }

    // Si déjà en train de sauvegarder → attendre la fin
    if (isSavingRef.current) {
      return new Promise<void>((resolve) => {
        flushResolveRef.current = resolve;
      });
    }

    // Exécuter immédiatement
    return new Promise<void>((resolve) => {
      flushResolveRef.current = resolve;
      executeSave(dataRef.current);
    });
  }, [status, executeSave]);

  // Clear error
  const clearError = useCallback(() => {
    setError(null);
    setValidationErrors(null);
    setStatus('idle');
  }, []);

  return {
    status,
    error,
    validationErrors,
    flush,
    clearError,
  };
}

export default useAutoSave;
